import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PropertyUserMetadata } from "@/models/properties.model";
import { PropertyUserDisplayModel } from "./property-users.entity";
import {
    createPropertyContact,
    createPropertyUser,
    deletePropertyUser,
    patchPropertyUser,
    searchPropertyUsers,
} from "./property-users.api";
import { PropertyUsersRepository, type AssignableUser } from "@/repositories/property-users.repository";
import { uuid } from "@/interfaces";
import { toast } from "sonner";
import { failureMessage } from "@/repositories/property-people-records";

async function toastingFailure<T>(work: () => Promise<T>, fallback: string): Promise<T> {
    try {
        return await work();
    } catch (error) {
        toast.error(failureMessage(error, fallback));
        throw error;
    }
}

interface PropertyUsersState {
    users: PropertyUserDisplayModel[];
    assignableUsers: AssignableUser[];
    loading: boolean;
    saving: boolean;
    deleting: boolean;
    assigning: boolean;
    error: string | null;
    total: number;
    filters: {
        page: number;
        pageSize: number;
        sortBy: keyof PropertyUserDisplayModel;
        sortDirection: "asc" | "desc";
    };
}

const initialState: PropertyUsersState = {
    users: [],
    assignableUsers: [],
    loading: false,
    saving: false,
    deleting: false,
    assigning: false,
    error: null,
    total: 0,
    filters: {
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: "desc",
    },
};

export const fetchPropertyUsers = createAsyncThunk(
    "propertyUsers/fetch",
    async (propertyId: uuid) => toastingFailure(() => searchPropertyUsers(propertyId), "The property's contacts could not be loaded.")
);

export const fetchAssignableUsers = createAsyncThunk(
    "propertyUsers/fetchAssignableUsers",
    async () => {
        return await PropertyUsersRepository.listAssignableUsers();
    }
);

export const addPropertyUser = createAsyncThunk(
    "propertyUsers/add",
    async ({
               propertyId,
               userId,
               metaData,
               isPrimaryContact,
           }: {
        propertyId: uuid;
        userId: uuid;
        metaData: PropertyUserMetadata;
        isPrimaryContact: boolean;
    }) => {
        await toastingFailure(() => createPropertyUser(propertyId, userId, metaData, isPrimaryContact), "The user could not be assigned.");
        return propertyId;
    }
);

export const addPropertyContact = createAsyncThunk(
    "propertyUsers/addContact",
    async ({ propertyId, email, metaData, isPrimaryContact }: { propertyId: uuid; email: string; metaData: PropertyUserMetadata; isPrimaryContact: boolean }) => {
        await toastingFailure(() => createPropertyContact(propertyId, email, metaData, isPrimaryContact), "The contact could not be added.");
        return propertyId;
    }
);

export const updatePropertyUser = createAsyncThunk(
    "propertyUsers/update",
    async ({
               propertyId,
               id,
               metaData,
               isPrimaryContact,
           }: {
        propertyId: uuid;
        id: uuid;
        metaData: PropertyUserMetadata;
        isPrimaryContact: boolean;
    }) => {
        await toastingFailure(() => patchPropertyUser(propertyId, id, metaData, isPrimaryContact), "The contact could not be saved.");
        return id;
    }
);

export const removePropertyUser = createAsyncThunk(
    "propertyUsers/remove",
    async ({ propertyId, id }: { propertyId: uuid; id: uuid }) => {
        await toastingFailure(() => deletePropertyUser(propertyId, id), "The user could not be unassigned.");
        return id;
    }
);

const slice = createSlice({
    name: "propertyUsers",
    initialState,
    reducers: {
        setPage(state, action: PayloadAction<number>) {
            state.filters.page = action.payload;
        },
        setPageSize(state, action: PayloadAction<number>) {
            state.filters.pageSize = action.payload;
        },
        setSort(
            state,
            action: PayloadAction<{ sortBy: keyof PropertyUserDisplayModel; sortDirection: "asc" | "desc" }>
        ) {
            state.filters.sortBy = action.payload.sortBy;
            state.filters.sortDirection = action.payload.sortDirection;
        },
        resetFilters(state) {
            state.filters.page = 1;
            state.filters.pageSize = 10;
            state.filters.sortBy = "created_at";
            state.filters.sortDirection = "desc";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPropertyUsers.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchPropertyUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload;
                state.total = action.payload.length;
            })
            .addCase(fetchPropertyUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? null;
            })
            .addCase(addPropertyUser.pending, (state) => {
                state.saving = true;
            })
            .addCase(addPropertyUser.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(addPropertyUser.rejected, (state) => {
                state.saving = false;
            })
            .addCase(addPropertyContact.pending, (state) => {
                state.saving = true;
            })
            .addCase(addPropertyContact.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(addPropertyContact.rejected, (state) => {
                state.saving = false;
            })
            .addCase(updatePropertyUser.pending, (state) => {
                state.saving = true;
            })
            .addCase(updatePropertyUser.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(updatePropertyUser.rejected, (state) => {
                state.saving = false;
            })
            .addCase(removePropertyUser.pending, (state) => {
                state.deleting = true;
            })
            .addCase(removePropertyUser.fulfilled, (state) => {
                state.deleting = false;
            })
            .addCase(removePropertyUser.rejected, (state) => {
                state.deleting = false;
            })
            .addCase(fetchAssignableUsers.pending, (state) => {
                state.assigning = true;
            })
            .addCase(fetchAssignableUsers.fulfilled, (state, action) => {
                state.assigning = false;
                state.assignableUsers = action.payload;
            })
            .addCase(fetchAssignableUsers.rejected, (state) => {
                state.assigning = false;
            });
    },
});

export const { setPage, setPageSize, setSort, resetFilters } = slice.actions;
export default slice.reducer;